import React, { useEffect, useRef, useState } from "react";
import { PageHeader, BrutalCard } from "../components/brutal";
import PermissionsForm from "../components/PermissionsForm";
import { useAuth } from "../lib/auth";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { User, Camera, RotateCcw, Loader2, Bell, BellOff, Trophy, Copy, ExternalLink, Linkedin } from "lucide-react";
import { isPushSupported, subscribePush, unsubscribePush, getSubscription } from "../lib/push";

function AvatarEditor() {
  const { user, refreshMe } = useAuth();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [previewBust, setPreviewBust] = useState(Date.now());

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Image must be smaller than 5 MB");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", f);
      await api.post("/auth/me/avatar", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refreshMe();
      setPreviewBust(Date.now());
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const reset = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.delete("/auth/me/avatar");
      await refreshMe();
      setPreviewBust(Date.now());
      toast.success("Reset to default avatar");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const src = user?.avatar_url
    ? `${user.avatar_url}${user.avatar_url.includes("?") ? "&" : "?"}cb=${previewBust}`
    : `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(user?.name || "U")}`;

  return (
    <BrutalCard className="p-6 bg-brand-green" data-testid="avatar-editor">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-11 h-11 bg-white border-2 border-black rounded-xl flex items-center justify-center shadow-brutal">
          <Camera className="w-5 h-5" strokeWidth={2.4} />
        </div>
        <h3 className="font-display font-black text-xl">Profile photo</h3>
      </div>

      <div className="flex items-center gap-5">
        <div className="relative flex-shrink-0">
          <img
            key={previewBust}
            src={src}
            alt="Your avatar"
            crossOrigin="anonymous"
            className="w-24 h-24 rounded-2xl border-2 border-black object-cover bg-white shadow-brutal"
            data-testid="avatar-preview"
          />
          {busy && (
            <div className="absolute inset-0 bg-black/40 rounded-2xl border-2 border-black flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-white animate-spin" />
            </div>
          )}
        </div>
        <div className="flex-1 space-y-2">
          <p className="text-sm text-neutral-800">
            Shows up on the leaderboard, your Spotlight card, and your badges. PNG, JPG, WebP, or GIF — up to 5 MB.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onPick}
              disabled={busy}
              className="btn-brutal btn-primary text-sm"
              data-testid="avatar-upload-btn"
            >
              <Camera className="w-4 h-4" /> {busy ? "Uploading…" : "Upload new"}
            </button>
            <button
              type="button"
              onClick={reset}
              disabled={busy}
              className="btn-brutal btn-white text-sm"
              data-testid="avatar-reset-btn"
            >
              <RotateCcw className="w-4 h-4" /> Use default
            </button>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            className="hidden"
            onChange={onFile}
            data-testid="avatar-file-input"
          />
        </div>
      </div>
    </BrutalCard>
  );
}

function PushNotificationsCard() {
  const supported = isPushSupported();
  const [state, setState] = useState("loading"); // loading | disabled | enabled | unsupported
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported) { setState("unsupported"); return; }
    let mounted = true;
    (async () => {
      const perm = typeof Notification !== "undefined" ? Notification.permission : "default";
      const sub = await getSubscription();
      if (!mounted) return;
      setState(sub && perm === "granted" ? "enabled" : "disabled");
    })();
    return () => { mounted = false; };
  }, [supported]);

  const enable = async () => {
    setBusy(true);
    try {
      await subscribePush();
      setState("enabled");
      toast.success("Browser notifications on");
    } catch (err) {
      toast.error(err.message || "Couldn't enable notifications");
    } finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribePush();
      setState("disabled");
      toast.success("Browser notifications off");
    } catch (err) {
      toast.error(err.message || "Couldn't turn off notifications");
    } finally { setBusy(false); }
  };

  return (
    <BrutalCard className="p-6" data-testid="push-card">
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-11 h-11 border-2 border-black rounded-xl flex items-center justify-center shadow-brutal ${state === "enabled" ? "bg-brand-green" : "bg-white"}`}>
          {state === "enabled" ? <Bell className="w-5 h-5" strokeWidth={2.4} /> : <BellOff className="w-5 h-5" strokeWidth={2.4} />}
        </div>
        <h3 className="font-display font-black text-xl">Browser notifications</h3>
      </div>
      {state === "unsupported" ? (
        <p className="text-sm text-neutral-700">
          Your browser doesn't support web push notifications. On iOS, add ReferPro to your Home Screen to enable them.
        </p>
      ) : (
        <>
          <p className="text-sm text-neutral-700 mb-4">
            Get a desktop or phone notification when you earn points, unlock a badge, or get a shout-out — even when the tab is closed.
          </p>
          <div className="flex items-center justify-between gap-3">
            <span
              className={`chip ${state === "enabled" ? "bg-brand-green" : "bg-white"} text-xs`}
              data-testid="push-state"
            >
              {state === "loading" ? "Checking…" : state === "enabled" ? "Enabled" : "Off"}
            </span>
            {state === "enabled" ? (
              <button onClick={disable} disabled={busy} className="btn-brutal btn-white text-sm" data-testid="push-disable">
                <BellOff className="w-4 h-4" /> Turn off
              </button>
            ) : (
              <button onClick={enable} disabled={busy || state === "loading"} className="btn-brutal btn-primary text-sm" data-testid="push-enable">
                <Bell className="w-4 h-4" /> Enable
              </button>
            )}
          </div>
        </>
      )}
    </BrutalCard>
  );
}

function TrophyShareCard({ user }) {
  const isPublic = user?.permissions?.allow_public_profile !== false;
  const url = `${typeof window !== "undefined" ? window.location.origin : ""}/u/${user?.id}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Trophy room link copied");
    } catch {
      toast.error("Couldn't copy — long-press the URL to copy.");
    }
  };

  const shareLinkedin = () => {
    const text = `My referral wins on ReferPro 🏆`;
    const w = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&summary=${encodeURIComponent(text)}`;
    window.open(w, "_blank", "noopener");
  };

  return (
    <BrutalCard className="p-6 bg-brand-blue text-white" data-testid="trophy-share-card">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-11 h-11 bg-white text-brand-blue border-2 border-black rounded-xl flex items-center justify-center shadow-brutal flex-shrink-0">
          <Trophy className="w-5 h-5" strokeWidth={2.4} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display font-black text-2xl leading-none">Your trophy room</h2>
          <p className="text-sm text-white/85 mt-1">A no-login public profile of your badges, hires and lifetime points — perfect for a LinkedIn bio.</p>
        </div>
      </div>

      {isPublic ? (
        <>
          <div className="flex items-center gap-2 bg-white text-black border-2 border-black rounded-xl px-3 py-2">
            <input
              readOnly
              value={url}
              onClick={(e) => e.target.select()}
              className="flex-1 bg-transparent outline-none text-xs sm:text-sm truncate"
              data-testid="trophy-share-url"
            />
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <button onClick={copy} className="btn-brutal btn-white text-xs" data-testid="trophy-share-copy">
              <Copy className="w-3.5 h-3.5" /> Copy link
            </button>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="btn-brutal btn-white text-xs"
              data-testid="trophy-share-open"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Preview
            </a>
            <button
              onClick={shareLinkedin}
              className="btn-brutal text-xs bg-[#0A66C2] text-white border-black"
              data-testid="trophy-share-linkedin"
            >
              <Linkedin className="w-3.5 h-3.5" /> Share to LinkedIn
            </button>
          </div>
        </>
      ) : (
        <p className="text-sm text-white/90 italic" data-testid="trophy-share-disabled">
          Your trophy room is currently <strong>private</strong>. Toggle "Public trophy page" on below to enable a shareable link.
        </p>
      )}
    </BrutalCard>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  const isEmployee = user?.role === "employee";
  return (
    <div>
      <PageHeader
        title="Settings"
        subtitle="Control what's visible, shareable, and public. Your wins are yours first."
      />

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <AvatarEditor />
          {isEmployee && <TrophyShareCard user={user} />}
          <PushNotificationsCard />
          <BrutalCard className="p-6">
            <h2 className="font-display font-black text-2xl mb-5">Privacy &amp; sharing</h2>
            <PermissionsForm />
          </BrutalCard>
        </div>

        <BrutalCard className="p-6 bg-brand-yellow">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 bg-white border-2 border-black rounded-xl flex items-center justify-center shadow-brutal">
              <User className="w-5 h-5" strokeWidth={2.4} />
            </div>
            <h3 className="font-display font-black text-xl">Your account</h3>
          </div>
          <div className="space-y-3 text-sm">
            <div>
              <div className="text-[11px] uppercase tracking-widest font-bold text-neutral-700">Name</div>
              <div className="font-semibold">{user?.name}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest font-bold text-neutral-700">Email</div>
              <div className="font-semibold break-all">{user?.email}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest font-bold text-neutral-700">Team</div>
              <div className="font-semibold">{user?.team || user?.office || "—"}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest font-bold text-neutral-700">Total points</div>
              <div className="font-display font-black text-2xl">{user?.total_points || 0}</div>
            </div>
          </div>
          <div className="text-xs text-neutral-700 mt-5 border-t-2 border-black/20 pt-3">
            Need to change your name or email? Ask an admin.
          </div>
        </BrutalCard>
      </div>
    </div>
  );
}
